import React, { useEffect, useState } from 'react';
import { Button, CircularProgress } from '@material-ui/core';
import { ArrowDropDown, CheckCircle, Close } from '@material-ui/icons';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as lib from '~/dependencies';
import {
  useUserInfo,
  useOutboundNumbers,
  useSingleRenderNotification,
} from '~/hooks';
import { hasOnlyNumber, eventTracker, log } from '~/helpers';
import { findFlag } from '~/helpers/countryCodeAndFlagSelected';
import { BoxSelector, Title, Values, Row, DrawerComponent } from './styles';

const countries = require('~/assets/countries/countries.json');

/**
 *  Find a country flag index based on a given phone number
 */
function findCountryFlagIndex(countriesFlagList, phoneNumber) {
  try {
    if (countriesFlagList?.length && hasOnlyNumber(phoneNumber)) {
      const phoneNumberInfo = parsePhoneNumberFromString(
        String(`+${phoneNumber}`)
      );

      if (!phoneNumberInfo) return -1;

      const flagIndex = countriesFlagList.findIndex(e =>
        e.code
          ? e.code === phoneNumberInfo?.country
          : e.callingCode === phoneNumberInfo?.countryCallingCode
      );

      return flagIndex;
    }
  } catch (err) {
    log.error('Failure to find the flag index. Reason: ', err);
  }

  return -1;
}

/**
 *  RETUNRS outbound caller number as national
 *  WHEN user country matches the outbound number country.
 */
function showOutboundCallerNumberAsNational(userCountry, phoneNumber) {
  const phoneNumberInfo = parsePhoneNumberFromString(String(`+${phoneNumber}`));

  if (!phoneNumberInfo) return '';

  if (phoneNumberInfo?.country === userCountry) {
    return phoneNumberInfo.formatNational();
  }

  return phoneNumberInfo.formatInternational();
}

export default function CallerIdSelector() {
  // STATES
  const [loading, setLoading] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // HOOKS
  const [userInfo, setUserInfo] = useUserInfo([]);
  const [outboundNumbers, setOutboundActions] = useOutboundNumbers([]);
  const { showNotification } = useSingleRenderNotification();
  const { t, i18n } = lib.useTranslation();

  const allCountries = Object.keys(countries).reduce(
    (prev, next) =>
      prev.concat({
        code: next,
        callingCode: countries[next].callingCode,
        flag: countries[next].flag,
        name: countries[next].name.common,
      }),
    []
  );

  const outboundNumbersList = Array.isArray(outboundNumbers?.numbers)
    ? outboundNumbers.numbers
    : [];

  const outboundAllowedNumbersList = userInfo?.user_admin
    ? outboundNumbersList
    : outboundNumbersList.map(numberInfo => {
        if (numberInfo.selected) {
          if (
            userInfo?.user_permissions_access?.numbers?.includes(
              Number(numberInfo.number)
            )
          ) {
            return numberInfo;
          }

          return {
            ...numberInfo,
            name: undefined,
          };
        }

        return numberInfo;
      });

  const selectedOutboundInfo = outboundAllowedNumbersList?.find(
    e => e.selected
  );

  const outboundNumber = selectedOutboundInfo?.number;
  const outboundName = selectedOutboundInfo?.name;
  const outboundIntNumber = selectedOutboundInfo?.int_number;

  const callerIdFlagIndex = findCountryFlagIndex(
    allCountries,
    outboundIntNumber
  );

  let outboundNumbersInternational = [];

  // EFFECTS
  useEffect(() => {
    async function init() {
      setLocalLoading(true);
      await setUserInfo();
      await setOutboundActions('fetch');
      setLocalLoading(false);
    }

    init();
  }, [setUserInfo, setOutboundActions]);

  /**
   * This method changes your caller number "outbound_cli" in order to make or receive calls on a given number
   */
  async function handleChangeOutboundCallerNumber(number) {
    toggleDrawer(false);
    setLoading(true);

    await setOutboundActions('update', number);

    setTimeout(async () => {
      // NWA-1882: Allow database a half second before fetch updated user info
      await setUserInfo();

      setLoading(false);
    }, 500);

    eventTracker(events => events.DialerChangeCallerID);
  }

  function handleToggleDrawer() {
    if (outboundNumbersInternational?.length > 1) toggleDrawer(true);
  }

  function toggleDrawer(value) {
    setOpen(value);
  }

  // FIXME: Check possibility to move into userEffect
  try {
    outboundNumbersInternational = outboundAllowedNumbersList?.map(e => {
      const phoneNumberItemInfo = parsePhoneNumberFromString(
        String(`+${e?.int_number}`)
      );

      const numberName =
        phoneNumberItemInfo?.number && e.name
          ? `${phoneNumberItemInfo.number} - ${e.name}`
          : phoneNumberItemInfo?.number;

      const countryCode = phoneNumberItemInfo?.countryCallingCode || null;

      const countryName = phoneNumberItemInfo?.country || null;

      return {
        int_number: e.int_number,
        name: e.name,
        number: e.number,
        numberName,
        countryCode,
        countryName,
      };
    });
  } catch (err) {
    log.error('Failed to execute the CallerIdSelector component.', err);

    if (err?.message?.includes('formatInternational')) {
      showNotification(
        'error',
        ' Error converting number to international format.'
      );
    }
  }

  return (
    <>
      <Button onClick={handleToggleDrawer}>
        <BoxSelector loading={loading ? 1 : 0}>
          {loading ? (
            <CircularProgress size={14} />
          ) : (
            <>
              <Title lang={i18n.language}>
                <span>{t('components.callerIdSelector.callerId')}</span>
              </Title>
              <Values>
                {callerIdFlagIndex !== -1 && hasOnlyNumber(outboundNumber) ? (
                  <>
                    <Row>
                      <img
                        src={allCountries[callerIdFlagIndex].flag}
                        alt="flag"
                        style={{ maxWidth: 10, marginRight: 5 }}
                      />
                      <span className="outbound-phone-number">
                        {showOutboundCallerNumberAsNational(
                          userInfo?.user_country,
                          outboundIntNumber
                        )}
                      </span>
                    </Row>

                    {outboundName ? (
                      <Row>
                        <span className="outbound-phone-name">
                          {outboundName}
                        </span>
                      </Row>
                    ) : null}
                  </>
                ) : (
                  <>
                    {!hasOnlyNumber(outboundNumber) && !localLoading ? (
                      <span className="padding">Not Set (unknown/private)</span>
                    ) : (
                      <CircularProgress size={14} />
                    )}
                  </>
                )}
              </Values>
              <ArrowDropDown
                style={{
                  opacity: outboundNumbersInternational?.length > 1 ? 1 : 0,
                }}
              />
            </>
          )}
        </BoxSelector>
      </Button>
      <DrawerComponent
        anchor="top"
        open={open}
        onClose={() => toggleDrawer(false)}>
        <div id="boxTitle">
          <span className="title">
            {t('components.callerIdSelector.title')}
          </span>
          <Close className="close" onClick={() => toggleDrawer(false)} />
        </div>

        {outboundNumbersInternational &&
          Object.values(outboundNumbersInternational).map(e => {
            const countryOrCode = e.countryName || e.countryCode;

            const path = countryOrCode
              ? findFlag(countryOrCode, !!e.countryName)
              : '';

            return (
              <div
                className="rowSelect"
                key={e.number}
                value={e.number}
                onClick={() => handleChangeOutboundCallerNumber(e.number)}
                onKeyPress={() => handleChangeOutboundCallerNumber(e.number)}
                role="button"
                tabIndex={0}>
                <div className="left">
                  <img src={path} alt="flag" className="flag" />
                  <span>{e.numberName}</span>
                </div>
                <div className="right">
                  {userInfo.outbound_cli === e.number && (
                    <CheckCircle className="check-icon" />
                  )}
                </div>
              </div>
            );
          })}
      </DrawerComponent>
    </>
  );
}
